<template>
  <div class="full-page-width relative locations">
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :text="$t('location-info-text')"
          :title="$t('Locations')"
          :btnText="$t('Add location')"
          @action="openNewPopup"/>
      </div>
      <div class="col-xs-8">
        <ul class="list-table">
          <li
            class="list-table-item"
            v-for="(item, index) in get_senders.items" :key="`item-${index}`"
            @click="openEditPopup(item.id)">
            <div class="table-cell small">
              <div class="circled-icon flags">
                <CountryFlag :country='getFlag(item.country_id)' size='normal'/>
              </div>
            </div>
            <div class="table-cell">
              <p>
              <span class="main-info">
                <span v-if="item.company_name !== null">
                  {{ `${item.company_name}, ` }}
                </span>
               {{
                  `${item.name}, ${item.address_1}, ${item.post_code} ${item.city}, ${getCountryName(item.country_id)}`
                }}
              </span>
                <span class="additional-info">
                  {{ $t(get_senders.entity.type[item.type]) }}
                </span>
              </p>
            </div>
            <div class="table-cell small" v-show="item.is_default">
              <div class="list-icon-block">
                <Icon
                  :circle=true
                  name="star"
                  size="20"/>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <Popup
      v-if="openPopup"
      :popupMedium=true
      :isPopupSubmitDisabled="isPopupSubmitDisabled"
      :showConfirmButton="!deleteLocation"
      :showDeleteButton="deleteLocation"
      @cancel="togglePopup"
      @deleteAction="deleteLocationAction"
      @confirm="submitForm">
      <template v-slot:header>
        <h2 v-if="!isEdit">{{ $t('Add location') }}</h2>
        <h2 v-if="isEdit">{{ $t('Edit location') }}</h2>
      </template>
      <div>
        <div class="row" v-if="get_user.is_admin">
          <div class="col-xs-12">
            <InputSelect
              class="m-b-20"
              :multiSelect=true
              :preselected="form.accessible_for_subaccounts"
              :label="$t('accessible_for_accounts')"
              @change="selectAccessibleSubaccount"
              :options="getAllowedSubaccounts"
              :rules="{required: true}"/>
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <div class="flex-center toggle-title">
              <div>
                <p>{{ $t('is_default') }}</p>
                <span>{{ $t('Will be selected as shipper on booking page') }}</span>
              </div>
              <div>
                <Toggle id="is_default" v-model="form.is_default"/>
              </div>
            </div>
          </div>
        </div>
        <div class="separator medium"></div>
        <div class="row">
          <div class="col-xs-12 m-b-20">
            <InputSelect
              :label="$t('type')"
              :multiSelect=true
              :preselected="form.type"
              :rules="{required: true}"
              @change="selectType"
              :options="selectOptions(get_senders.entity['type'], true)"/>
          </div>
          <div class="col-xs-6 m-b-20">
            <InputSelect
              :label="$t('contact_type')"
              v-model="form.contact_type"
              :rules="{required: true}"
              :options="selectOptions(get_senders.entity['contact_type'], true)"/>
          </div>
          <div class="col-xs-6 m-b-20" v-if="parseInt(form.contact_type) === 1">
            <InputText
              :label="$t('company_name')"
              v-model="form.company_name"
              :rules="{required: parseInt(form.contact_type) === 1}"/>
          </div>
          <div class="col-xs-6 m-b-20">
            <InputText
              :label="$t('name')"
              v-model="form.name"
              :rules="{required: false}"/>
          </div>
          <div class="col-xs-6 m-b-20">
            <InputText
              :label="$t('vat')"
              v-model="form.vat"
              :rules="{required: false}"/>
          </div>
        </div>
        <div>
          <AddressPicker
            class="m-b-20"
            :label="$t('address_1')"
            @addressSelected="changeAddress"
            v-model="formAddress"/>
        </div>
        <div class="row">
          <div class="col-xs-6">
            <PhoneSelect v-model="form.phone" :rules="{required: false}"/>
          </div>
          <div class="col-xs-6">
            <InputText
              label="Email"
              v-model="form.email"
              :rules="{required: false, email: false}"/>
          </div>
        </div>
        <div class="separator medium"></div>

        <div v-if="!(form.type && form.type.length === 1 && String(form.type[0]) === '3')">
          <!-- Additional Shipping Information Section -->
          <h3 class="m-b-20">{{ $t('additional_information') }}</h3>

          <!-- INCOTERMS -->
          <div class="row">
            <div class="col-xs-6 m-b-20">
              <InputSelect
                :label="$t('Incoterms')"
                v-model="additionalShippingInfo.goods_incoterm"
                @change="checkDutyAndCarrierNo"
                :options="incotermsOptions"
              />
            </div>
            <!-- DUTY PAID BY -->
            <div
              v-if="additionalShippingInfo.goods_incoterm === 'DDP'"
              class="col-xs-6 m-b-20">
              <InputSelect
                :label="$t('duty_paid_by')"
                v-model="additionalShippingInfo.duty_paid_by"
                @change="checkCarrierNo"
                :options="dutyPaidByOptions"
              />
            </div>
          </div>

          <!-- CARRIER NUMBER -->
          <div class="row"
               v-if="(additionalShippingInfo.duty_paid_by === 3 || additionalShippingInfo.duty_paid_by === 2) && additionalShippingInfo.goods_incoterm === 'DDP'">
            <div class="col-xs-12 m-b-20">
              <InputText
                v-model="additionalShippingInfo.duty_payer_account_no"
                :label="$t('Carrier account no.')"
              />
            </div>
          </div>

          <!-- FREIGHT PAID BY -->
          <div class="row">
            <div class="col-xs-6 m-b-20">
              <InputSelect
                :label="$t('freight_paid_by')"
                v-model="additionalShippingInfo.freight_paid_by"
                @change="checkFreightandCustomerInfo"
                :options="freightPaidByOptions"
              />
            </div>
            <!-- FREIGHT NUMBER -->
            <div
              v-if="additionalShippingInfo.freight_paid_by === 2 || additionalShippingInfo.freight_paid_by === 3"
              class="col-xs-6 m-b-20">
              <InputText
                v-model="additionalShippingInfo.freight_payer_account_no"
                :label="$t('Freight Carrier account no.')"
              />
            </div>
            <div
              v-if="additionalShippingInfo.freight_paid_by === 4"
              class="col-xs-6 m-b-20">
              <InputSelect
                :label="$t('LBL_PAYERS')"
                v-model="additionalShippingInfo.freight_payer_id"
                :options="get_payer_select"
              />
            </div>
          </div>

          <!-- Customs Information -->
          <div class="row">
            <div class="col-xs-6 m-b-20">
              <InputSelect
                :label="$t('LBL_REASON_FOR_EXPORT')"
                v-model="additionalShippingInfo.invoice_type"
                :options="invoiceTypeOptions"
              />
            </div>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                :multiSelect="true"
                :label="$t('Additional info')"
                v-model="selectedInfoItems"
                @input="selectAdditionalInfo"
                :options="additionalInfoOptions"
              />
            </div>
          </div>

          <!-- Conditional fields based on selected additional info -->
          <div class="row" v-if="selectedInfoItems.indexOf('22') > -1">
            <div class="col-xs-12 m-b-20">
              <InputText
                :label="$t('Text')"
                v-model="additionalShippingInfo.customs_additional_info_text"
              />
            </div>
          </div>

          <div class="row" v-if="selectedInfoItems.indexOf('2') > -1">
            <div class="col-xs-12 m-b-20">
              <InputText
                :label="$t('ECCN number')"
                v-model="additionalShippingInfo.eccn"
              />
            </div>
          </div>

          <div class="row" v-if="selectedInfoItems.indexOf('3') > -1">
            <div class="col-xs-6 m-b-20">
              <InputText
                :label="$t('EEI number')"
                v-model="additionalShippingInfo.eei_number"
              />
            </div>
            <div class="col-xs-6 m-b-20">
              <InputText
                :label="$t('Tin number')"
                v-model="additionalShippingInfo.tin_number"
              />
            </div>
            <div class="col-xs-12 m-b-20">
              <InputSelect
                :label="$t('Tin type')"
                v-model="additionalShippingInfo.tin_type"
                :options="tinTypeOptions"
              />
            </div>
          </div>

          <div class="row" v-if="selectedInfoItems.indexOf('1') > -1">
            <div class="col-xs-12 m-b-20">
              <InputText
                :label="$t('EORI number')"
                v-model="additionalShippingInfo.eori"
              />
            </div>
          </div>

          <div class="separator medium"></div>

        <!-- Pickup Time Section -->
        <div class="row">
          <div>
            <h1 class="m-b-10 m-l-10">{{ $t('monday_pickup_range') }}</h1>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-model="pickupTime.monday.from"
                :rules="{required: false}"
                :options="getPickupTimeOptions()"/>
            </div>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-if="pickupTime.monday.from"
                v-model="pickupTime.monday.to"
                :rules="{required: true}"
                :options="getPickupTimeOptions(pickupTime.monday.from)"/>
            </div>
          </div>
        </div>
        <div class="row">
          <div>
            <h1 class="m-b-10 m-l-10">{{ $t('tuesday_pickup_range') }}</h1>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-model="pickupTime.tuesday.from"
                :rules="{required: false}"
                :options="getPickupTimeOptions()"/>
            </div>
            <div class="col-xs-6">
              <InputSelect
                v-if="pickupTime.tuesday.from"
                v-model="pickupTime.tuesday.to"
                :rules="{required: true}"
                :options="getPickupTimeOptions(pickupTime.tuesday.from)"/>
            </div>
          </div>
        </div>
        <div class="row">
          <div>
            <h1 class="m-b-10 m-l-10">{{ $t('wednesday_pickup_range') }}</h1>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-model="pickupTime.wednesday.from"
                :rules="{required: false}"
                :options="getPickupTimeOptions()"/>
            </div>
            <div class="col-xs-6">
              <InputSelect
                v-if="pickupTime.wednesday.from"
                v-model="pickupTime.wednesday.to"
                :rules="{required: true}"
                :options="getPickupTimeOptions(pickupTime.wednesday.from)"/>
            </div>
          </div>
        </div>
        <div class="row">
          <div>
            <h1 class="m-b-10 m-l-10">{{ $t('thursday_pickup_range') }}</h1>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-model="pickupTime.thursday.from"
                :rules="{required: false}"
                :options="getPickupTimeOptions()"/>
            </div>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-if="pickupTime.thursday.from"
                v-model="pickupTime.thursday.to"
                :rules="{required: true}"
                :options="getPickupTimeOptions(pickupTime.thursday.from)"/>
            </div>
          </div>
        </div>
        <div class="row">
          <div>
            <h1 class="m-b-10 m-l-10">{{ $t('friday_pickup_range') }}</h1>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-model="pickupTime.friday.from"
                :rules="{required: false}"
                :options="getPickupTimeOptions()"/>
            </div>
            <div class="col-xs-6 m-b-20">
              <InputSelect
                v-if="pickupTime.friday.from"
                v-model="pickupTime.friday.to"
                :rules="{required: true}"
                :options="getPickupTimeOptions(pickupTime.friday.from)"/>
            </div>
          </div>
        </div>

        <div class="separator medium"></div>
        </div>
        <div class="col-xs-12 p-b-20" v-if="isEdit">
          <div class="flex-center toggle-title">
            <div>
              <p>{{ $t('delete') }}</p>
              <span>{{ $t('Location will be deleted') }}</span>
            </div>
            <div>
              <Toggle color="red" id="delete" v-model="deleteLocation"/>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import mixin from '@/mixins/mixins.js';
import date from '@/mixins/date.js'
import AddressPicker from "@/components/global/addressPicker/index.vue";
import PhoneSelect from "@/components/elements/inputs/phone.vue";
import CountryFlag from 'vue-country-flag';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import Popup from '@/components/global/popup';
import InputSelect from '@/components/elements/inputs/select.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import countryList from '@/data/country.js';
import {
  PICKUP_SLOT_INTERVAL_MINUTES,
  PICKUP_WINDOW_GRID_END,
  PICKUP_WINDOW_GRID_START,
  generatePickupSlots,
} from '@/data/pickupTime';
import i18n from '@/i18n';

import InfoTextBox from '@/components/global/infoTextBox';
import formValidator from '@/mixins/formValidator';

export default {
  name: 'Senders',
  mixins: [mixin, formValidator, date],
  components: {
    Btn,
    AddressPicker,
    PhoneSelect,
    Popup,
    InputText,
    CountryFlag,
    InputSelect,
    Toggle,
    InfoTextBox,
  },
  data() {
    return {
      openPopup: false,
      deleteLocation: false,
      isEdit: false,
      senderId: '',
      formAddress: {
        address_1: '',
        address_2: '',
        post_code: '',
        city: '',
        country_id: '',
        state: '',
      },
      form: {
        accessible_for_subaccounts: [],
        name: '',
        company_name: '',
        phone: {
          number: '',
          country: '',
        },
        email: '',
        vat: '',
        is_default: '',
        type: [],
        contact_type: '',
      },
      // Additional shipping information
      additionalShippingInfo: {
        goods_incoterm: '',
        duty_paid_by: '',
        duty_payer_account_no: '',
        freight_paid_by: '',
        freight_payer_account_no: '',
        freight_payer_id: '',
        invoice_type: '',
        eori: '',
        eccn: '',
        customs_additional_info_text: '',
        eei_number: '',
        tin_number: '',
        tin_type: '',
      },
      selectedInfoItems: [],
      pickupTime: {
        monday: {
          from: '',
          to: '',
        },
        tuesday: {
          from: '',
          to: '',
        },
        wednesday: {
          from: '',
          to: '',
        },
        thursday: {
          from: '',
          to: '',
        },
        friday: {
          from: '',
          to: '',
        },
      },
      //Pickup window options. Slot step comes from '@/data/pickupTime' so that the
      //defaults written here stay on the same grid as the booking popups.
      timeStamps: generatePickupSlots(
        PICKUP_WINDOW_GRID_START,
        PICKUP_WINDOW_GRID_END,
        PICKUP_SLOT_INTERVAL_MINUTES,
      ),
      // Additional shipping info options
      incotermsOptions: [
        { text: `${this.$t('DAP')}`, value: 'DAP'},
        { text: `${this.$t('DDP')}`, value: 'DDP'},
        { text: `${this.$t('EXW')}`, value: 'EXW'},
        { text: `${this.$t('CPT')}`, value: 'CPT'},
      ],
      dutyPaidByOptions: [
        { text: `${this.$t('Seller')}`, value: 1},
        { text: `${this.$t('Reciever')}`, value: 2},
        { text: `${this.$t('Third party')}`, value: 3},
      ],
      additionalInfoOptions: [
        {
          text: 'ECCN',
          value: '2',
        },
        {
          text: 'EEI',
          value: '3',
        },
        {
          text: 'TEXT',
          value: '22',
        },
      ],
      tinTypeOptions: [
        { text: `${i18n.t('BUSINESS_NATIONAL')}`, value: 1},
        { text: `${i18n.t('BUSINESS_STATE')}`, value: 2},
        { text: `${i18n.t('BUSINESS_UNION')}`, value: 3},
        { text: `${i18n.t('PERSONAL_NATIONAL')}`, value: 4},
        { text: `${i18n.t('PERSONAL_STATE')}`, value: 5},
      ],
      invoiceTypeOptions: [
        { text: `${i18n.t('Sample')}`, value: 1},
        { text: `${i18n.t('Sale')}`, value: 2},
        { text: `${i18n.t('Gift')}`, value: 3},
        { text: `${i18n.t('Return_1')}`, value: 4},
        { text: `${i18n.t('Repair')}`, value: 5},
        { text: `${i18n.t('Temporary export')}`, value: 6},
        { text: `${i18n.t('Exhibition goods')}`, value: 7},
        { text: `${i18n.t('Testing analysis')}`, value: 8},
      ],
    };
  },
  computed: {
    ...mapGetters([
      'get_senders',
      'get_sender_by_id',
      'get_subaccounts',
      'get_user',
      'get_payers',
      'get_payer_select'
    ]),
    isPopupSubmitDisabled() {
      if(this.get_user.is_admin && this.form.accessible_for_subaccounts.length === 0) return true;
      if (this.form.type.length === 0) return true;

      let formWithValidators = [
        {
          formFeild:'address_1',
          value: this.formAddress.address_1,
          validators:{required: true}
        },
        {
          formFeild:'post_code',
          value: this.formAddress.post_code,
          validators:{required: true}
        },
        {
          formFeild:'city',
          value: this.formAddress.city,
          validators:{required: true}
        },
        {
          formFeild:'country_id',
          value: this.formAddress.country_id,
          validators:{required: true}
        },
        {
          formFeild:'contact_type',
          value: this.form.contact_type,
          validators:{required: true}
        },
      ]
      if(this.form.contact_type == 1){
        formWithValidators.push({
          formFeild:'company_name',
          value: this.form.company_name,
          validators:{required: true}
        })
      } else {
        formWithValidators.filter((item) => item.formFeild != 'company_name')
      }

      return this.validateInput(formWithValidators);
    },
    getAllowedSubaccounts() {
      return this.get_subaccounts.filter((acc) => this.get_user.accessible_subaccounts_array.indexOf(String(acc.id)) > -1).map((acc) => ({text: acc.name, value: String(acc.id),}));
    },
    freightPaidByOptions() {
      const options = [];
      options.push({ text: `${this.$t('Seller')}`, value: 1})
      if (this.get_payers.items.length > 0) {
        options.push({ text: `${this.$t('LBL_PAYER_LIST')}`, value: 4})
      }
      options.push({ text: `${this.$t('Other')}`, value: 2})

      return options
    },
  },
  created() {
    this.$store.dispatch('GET_SENDERS');
    this.$store.dispatch('GET_PAYERS_OPTIONS');
  },
  methods: {
    // Additional shipping info methods
    checkDutyAndCarrierNo(val) {
      if(val === 'DDP') {
        this.additionalShippingInfo.duty_paid_by = 1;
      } else {
        this.additionalShippingInfo.duty_paid_by = '';
        this.additionalShippingInfo.duty_payer_account_no = '';
      }
    },
    checkCarrierNo(val) {
      if(parseInt(val) !== 3 && parseInt(val) !== 2) {
        this.additionalShippingInfo.duty_payer_account_no = '';
      }
    },
    checkFreightandCustomerInfo(val) {
      if(parseInt(val) !== 2 && parseInt(val) !== 3) {
        this.additionalShippingInfo.freight_payer_account_no = '';
      }
      if(parseInt(val) !== 4) {
        this.additionalShippingInfo.freight_payer_id = '';
      }
    },
    selectAdditionalInfo(val) {
      this.selectedInfoItems = val;

      // Clear fields if they're not selected
      if(this.selectedInfoItems.indexOf('2') < 0){
        this.additionalShippingInfo.eccn = '';
      }
      if(this.selectedInfoItems.indexOf('22') < 0){
        this.additionalShippingInfo.customs_additional_info_text = '';
      }
      if(this.selectedInfoItems.indexOf('3') < 0){
        this.additionalShippingInfo.eei_number = '';
        this.additionalShippingInfo.tin_number = '';
        this.additionalShippingInfo.tin_type = '';
      }
    },

    getPickupTimeOptions(startTime) {
      if (startTime) {
        startTime = this.addTime(startTime, '+01:00')
      }

      if (!startTime) {
        startTime = '06:00'
      }

      return this.timeStamps.filter((val) => {
        if (startTime <= val) {
          return val;
        }
      }).map((val, index) => {
        return {value: val, text: val}
      })
    },
    selectAccessibleSubaccount(val) {
      this.form.accessible_for_subaccounts = val;
    },
    selectType(val) {
      this.form.type = val;
    },
    getFlag(id) {
      return countryList.filter((opt) => opt.id === id.toString())[0].alpha2;
    },
    getCountryName(id) {
      return countryList.filter((opt) => opt.id === id.toString())[0].name;
    },
    selectOptions(obj, translate = false) {
      return Object.entries(obj).map(([key, value]) => {
        return { text: translate ? this.$t(value) : value, value: key };
      });
    },
    changeAddress(val) {
      this.formAddress.address_1 = val.address_1;
      this.formAddress.address_2 = val.address_2 || '';
      this.formAddress.country_id = val.country_id;
      this.formAddress.post_code = val.post_code;
      this.formAddress.city = val.city;
      this.formAddress.state = val.state;

      this.form.phone.country = this.getFlag(val.country_id);
    },
    clearLocalFormData() {
      this.formAddress = {
        address_1: '',
        address_2: '',
        post_code: '',
        city: '',
        country_id: '',
        state: '',
      };
      this.form = {
        accessible_for_subaccounts: [],
        name: '',
        company_name: '',
        phone: {
          number: '',
          country: '',
        },
        email: '',
        vat: '',
        is_default: false,
        type: [],
        contact_type: '',
      };
      this.additionalShippingInfo = {
        goods_incoterm: '',
        duty_paid_by: '',
        duty_payer_account_no: '',
        freight_paid_by: '',
        freight_payer_account_no: '',
        freight_payer_id: '',
        invoice_type: '',
        eori: '',
        eccn: '',
        customs_additional_info_text: '',
        eei_number: '',
        tin_number: '',
        tin_type: '',
      };
      this.selectedInfoItems = [];
      this.pickupTime = {
        monday: {
          from: '',
          to: '',
        },
        tuesday: {
          from: '',
          to: '',
        },
        wednesday: {
          from: '',
          to: '',
        },
        thursday: {
          from: '',
          to: '',
        },
        friday: {
          from: '',
          to: '',
        },
      }
    },
    togglePopup() {
      this.openPopup = !this.openPopup;
      this.senderId = '';
      this.clearLocalFormData();
    },
    submitForm() {
      const action = this.isEdit ? 'EDIT_SENDER' : 'SET_SENDER';
      this.form.accessible_for_subaccounts = this.form.accessible_for_subaccounts.filter((val) => this.get_user.accessible_subaccounts_array.indexOf(String(val)) > -1);

      this.$store.dispatch(action, {
        form: {
          accessible_for_subaccounts: this.form.accessible_for_subaccounts,
          address_1: this.formAddress.address_1,
          address_2: this.formAddress.address_2,
          post_code: this.formAddress.post_code,
          city: this.formAddress.city,
          country_id: this.formAddress.country_id,
          state: this.formAddress.state,
          name: this.form.name,
          company_name: this.form.company_name,
          phone: this.form.phone.number,
          email: this.form.email,
          vat: this.form.vat,
          is_default: Number(this.form.is_default),
          type: this.form.type,
          contact_type: this.form.contact_type,
          pickup_time: {...this.pickupTime},
          // Additional shipping information
          ...this.additionalShippingInfo,
          selected_info_items: JSON.stringify(this.selectedInfoItems),
          additional_info_updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
        },
        id: this.senderId || '',
      }).then(() => {
        this.$store.dispatch('GET_SENDERS');
        this.openPopup = false;
        this.clearLocalFormData();
      });
    },
    deleteLocationAction() {
      this.$store.dispatch('DELETE_SENDER', this.senderId).then(() => {
        this.$store.dispatch('GET_SENDERS');
        this.deleteLocation = false;
        this.isEdit = false;
        this.togglePopup();
      });
    },
    openNewPopup() {
      this.deleteLocation = false;
      this.isEdit = false;
      this.togglePopup();
    },
    openEditPopup(id) {
      this.deleteLocation = false;
      this.togglePopup();
      this.isEdit = true;
      this.senderId = id;
      const data = this.get_sender_by_id(id);

      this.form = {
        accessible_for_subaccounts: data.accessible_for_subaccounts,
        name: data.name,
        company_name: data.company_name,
        phone: {
          number: data.phone,
          country: '',
        },
        email: data.email,
        vat: data.vat,
        is_default: Number(data.is_default),
        type: data.type,
        contact_type: data.contact_type,
      };
      this.formAddress = {
        address_1: data.address_1,
        address_2: data.address_2,
        post_code: data.post_code,
        city: data.city,
        country_id: data.country_id,
        state: data.state,
      };

      // Load additional shipping info if available
      if (data.goods_incoterm) {
        this.additionalShippingInfo = {
          goods_incoterm: data.goods_incoterm || '',
          duty_paid_by: data.duty_paid_by || '',
          duty_payer_account_no: data.duty_payer_account_no || '',
          freight_paid_by: data.freight_paid_by || '',
          freight_payer_account_no: data.freight_payer_account_no || '',
          freight_payer_id: data.freight_payer_id || '',
          invoice_type: data.invoice_type,
          eori: data.eori || '',
          eccn: data.eccn || '',
          customs_additional_info_text: data.customs_additional_info_text || '',
          eei_number: data.eei_number || '',
          tin_number: data.tin_number || '',
          tin_type: data.tin_type || '',
        };

        // Parse selected info items if they exist
        if (data.selected_info_items) {
          this.selectedInfoItems = typeof data.selected_info_items === 'string'
            ? JSON.parse(data.selected_info_items)
            : data.selected_info_items;
        }
      }

      if (data.pickup_time) {
        this.pickupTime = data.pickup_time;
      }
    },
  },
  watch: {
    'formAddress.country_id': {
      handler(newVal){
        if(this.form.phone.number.length < 5 && String(newVal) !== ''){
          this.form.phone.country = this.getCountry(newVal, 'alpha2');
        }
      },
      deep: true
    },
  },
};
</script>
